/**
 * Thin fetch wrapper. No axios.
 *
 * Two jobs:
 *  1. Attach exactly one auth header, chosen explicitly by the caller. The staff
 *     JWT and the kiosk device token are never both on a request, and the caller
 *     has to say which it means (see docs/decisions.md D-006).
 *  2. Normalise every failure — HTTP error, portal error envelope, dead network,
 *     unparseable body — into one thrown `ApiError`, so screens have a single
 *     catch path instead of four.
 */

const BASE_URL: string = (
  import.meta.env.VITE_APP_SERVER_DOMAIN || "http://localhost:8000"
).replace(/\/+$/, "");

export type AuthMode = "staff" | "kiosk" | "none";

/** Portal's newer envelope. Legacy endpoints are normalised in kiosk.ts. */
export interface ApiEnvelope<T> {
  status: string;
  data: T;
  message?: string;
}

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly httpStatus: number;
  /** Server's `status` field where present; "network" / "http" as fallbacks. */
  readonly status: string;
  readonly data: unknown;

  constructor(opts: {
    httpStatus: number;
    status: string;
    message: string;
    data?: unknown;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.httpStatus = opts.httpStatus;
    this.status = opts.status;
    this.data = opts.data;
  }

  /** True when the request never got an answer — offline, DNS, CORS, timeout. */
  get isNetwork(): boolean {
    return this.httpStatus === 0;
  }

  /**
   * The device token was rejected. Callers clear it and bounce to /setup.
   *
   * Only the explicit `invalid_device` status counts — never a bare 401.
   * Eviction wipes the tablet's registration, and a 401 can come from
   * elsewhere: a kiosk and server a version apart (a route that moved, or
   * changed method, falls through to a staff-auth route answering
   * `401 { msg }`), or a proxy in front of the API. Those are faults to show
   * as "Connection problem", not a reason to unregister the device. The
   * server sends `status: "invalid_device"` on every kiosk-token rejection
   * (`sendInvalidDevice` in routes/attendance-kiosk.js), and authHeader()
   * above uses the same status when there is no token at all.
   */
  get isInvalidDevice(): boolean {
    return this.status === "invalid_device";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  auth: AuthMode;
  token?: string | null;
  /** Milliseconds before the request is abandoned. The kiosk must fail fast. */
  timeoutMs?: number;
  signal?: AbortSignal;
}

/*
  The base may carry a path — UAT/prod serve the API under /node
  (https://uat.zebrarobotics.com/node). `new URL("/api/x", base)` would DROP
  that path (an absolute path replaces the base's path wholesale), so the
  base's origin and path are joined by hand.
*/
const parsedBase = new URL(BASE_URL || window.location.origin);
const BASE_ORIGIN = parsedBase.origin;
const BASE_PATH = parsedBase.pathname.replace(/\/+$/, "");

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(
    `${BASE_PATH}${path.startsWith("/") ? path : `/${path}`}`,
    BASE_ORIGIN,
  );
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function authHeader(
  auth: AuthMode,
  token: string | null | undefined,
): Record<string, string> {
  if (auth === "none") return {};
  if (!token) {
    throw new ApiError({
      httpStatus: 0,
      status: auth === "kiosk" ? "invalid_device" : "unauthenticated",
      message:
        auth === "kiosk" ? "This device is not registered." : "You are not signed in.",
    });
  }
  // Raw token, no `Bearer` prefix — matches the portal's auth middleware.
  return auth === "kiosk" ? { "x-kiosk-token": token } : { "x-auth-token": token };
}

/**
 * Pull a human-usable message out of whatever the server sent back.
 * The portal is not consistent here, so try the known shapes in order.
 */
function messageFrom(payload: unknown, fallback: string): string {
  if (typeof payload === "string" && payload.trim()) return payload;
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    for (const key of ["message", "error", "msg"]) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) return value;
    }
  }
  return fallback;
}

function statusFrom(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object") {
    const value = (payload as Record<string, unknown>).status;
    if (typeof value === "string" && value) return value;
  }
  return fallback;
}

/**
 * Perform a request and return the parsed JSON body.
 *
 * Returns the raw body — callers unwrap it, because the portal mixes envelope
 * shapes (`{ status, data }`, `{ result }`, bare arrays, `{ accessToken }`).
 * Only *failures* are normalised here.
 */
export async function request<T = unknown>(
  path: string,
  options: RequestOptions,
): Promise<T> {
  const { method = "GET", body, query, auth, token, timeoutMs = 12_000 } = options;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...authHeader(auth, token),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  if (options.signal) {
    options.signal.addEventListener("abort", () => controller.abort(), {
      once: true,
    });
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
      cache: "no-store",
      credentials: "omit",
    });
  } catch (cause) {
    // Offline, DNS failure, CORS rejection, or our own abort.
    const aborted = cause instanceof DOMException && cause.name === "AbortError";
    throw new ApiError({
      httpStatus: 0,
      status: aborted ? "timeout" : "network",
      message: aborted
        ? "The server took too long to answer."
        : "Could not reach the server.",
      data: cause,
    });
  } finally {
    window.clearTimeout(timer);
  }

  const raw = await response.text();
  let payload: unknown = null;
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      // A proxy error page, an HTML 502, a truncated body. Not usable.
      payload = raw;
    }
  }

  if (!response.ok) {
    throw new ApiError({
      httpStatus: response.status,
      status: statusFrom(payload, response.status === 401 ? "unauthorized" : "http"),
      message: messageFrom(payload, `Request failed (${response.status}).`),
      data: payload,
    });
  }

  // A 200 can still carry an error envelope. `scan` in particular answers 200
  // for on_break, so the caller must be able to see the status — it is left in
  // the body and unwrapped by the caller, not thrown here.
  return payload as T;
}

/**
 * Unwrap `{ status: "success", data }`. Throws ApiError if the envelope carries
 * any other status, so happy-path callers can ignore the wrapper entirely.
 */
export function unwrap<T>(payload: unknown, httpStatus = 200): T {
  if (payload && typeof payload === "object" && "data" in payload) {
    const envelope = payload as ApiEnvelope<T>;
    if (envelope.status && envelope.status !== "success") {
      throw new ApiError({
        httpStatus,
        status: envelope.status,
        message: messageFrom(payload, "The server rejected that request."),
        data: envelope.data,
      });
    }
    return envelope.data;
  }
  throw new ApiError({
    httpStatus,
    status: "malformed",
    message: "The server sent back something unexpected.",
    data: payload,
  });
}

export { BASE_URL };
